
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Select, InputLabel,
  FormControl, Typography, Box, Grid, Divider, Paper, Snackbar,
  Alert, CircularProgress
} from "@mui/material";
import { confirmRide, addDriverInfo } from "../../services/api";

export default function RideManageDialog({ open, onClose, ride, onUpdated }) {
  const [status, setStatus] = useState(ride.isRideConfirmed || 0);
  const [driverMobile, setDriverMobile] = useState(ride.driverMobileNumber || "");
  const [pickupTime, setPickupTime] = useState(ride.pickupTime || "");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const isPaymentDone = ride.paymentDetails?.status?.toLowerCase() === "success";

  useEffect(() => {
    if (ride) {
      setStatus(ride.isRideConfirmed || 0);
      setDriverMobile(ride.driverMobileNumber || "");
      setPickupTime(ride.pickupTime || "");
    }
  }, [ride]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await confirmRide(ride.bookingId, status, ride.userId);

      if (status === 1 && driverMobile && pickupTime) {
        await addDriverInfo(ride.bookingId, driverMobile, pickupTime);
      }

      setLoading(false);
      setSnackbarOpen(true);

      setTimeout(() => {
        setSnackbarOpen(false);
        onClose();
        onUpdated();
      }, 2000);
    } catch (err) {
      console.error("Failed to update ride:", err);
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Manage Ride Booking</DialogTitle>

        <DialogContent dividers>
          {/* User Info */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>User Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><Typography><strong>Name:</strong> {ride.name}</Typography></Grid>
            <Grid item xs={6}><Typography><strong>Email:</strong> {ride.email}</Typography></Grid>
            <Grid item xs={6}><Typography><strong>Contact:</strong> {ride.contactNumber}</Typography></Grid>
            <Grid item xs={6}><Typography><strong>Fare:</strong> ₹{ride.fare}</Typography></Grid>
            <Grid item xs={12}><Typography><strong>Payment Status:</strong> {ride.paymentDetails?.status}</Typography></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Ride Info */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Ride Info</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><Typography><strong>Passenger Count:</strong> {ride.passengerCount}</Typography></Grid>
            <Grid item xs={6}><Typography><strong>Login Time:</strong> {ride.officeArrivalTime}</Typography></Grid>
            <Grid item xs={6}><Typography><strong>Logout Time:</strong> {ride.officeDepartureTime}</Typography></Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1, backgroundColor: "#f9f9f9" }}>
                <Typography variant="body2"><strong>Home Address:</strong><br />{ride.homeAddress}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1, backgroundColor: "#f9f9f9" }}>
                <Typography variant="body2"><strong>Office Address:</strong><br />{ride.officeAddress}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Admin Actions */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Admin Actions</Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>Ride Status</InputLabel>
            <Select
              label="Ride Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value={0}>Pending</MenuItem>
              <MenuItem value={1} disabled={!isPaymentDone}>Confirm</MenuItem>
              <MenuItem value={2}>Cancel</MenuItem>
            
            </Select>
          </FormControl>

          {/* Driver Info Section */}
          {status === 1 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Driver Info</Typography>
              <Paper elevation={1} sx={{ p: 2, backgroundColor: "#f0f4f8", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Driver Mobile Number"
                      value={driverMobile}
                      onChange={(e) => setDriverMobile(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Pickup Time"
                      type="time"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {!isPaymentDone && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              Cannot confirm ride until payment is completed.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary" disabled={loading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading || (status === 1 && (!driverMobile || !pickupTime || !isPaymentDone))}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          Ride updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
}
